import { trigger } from '@angular/animations';
import { NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { GalleryDto, TenantDto } from '@nusign/picflow-backend-types';
import { filter, Subject, switchMap } from 'rxjs';
import { WorkspaceDialogComponent } from '../../../account/dialogs/workspace-dialog/workspace-dialog.component';
import { dialogBottomInner, dialogBottomOuter } from '../../../animations/dialog-bottom.animation';
import { dialogInner } from '../../../animations/dialog-inner.animation';
import { ScreenSizes } from '../../../classes/screen-sizes.class';
import { DialogOptions } from '../../../interfaces/dialog-data.interface';
import { DialogService, OnGetData } from '../../../services/dialog.service';
import { GalleryService } from '../../../services/gallery.service';
import { StateService } from '../../../services/state.service';
import { TenantService } from '../../../services/tenant.service';
import { LoadingIndicatorComponent } from '../../../shared/components/loading-indicator/loading-indicator.component';
import { SmoothAutoHeightDirective } from '../../../shared/directives/smooth-auto-height.directive';
import { ShareDialogSocialFooterComponent } from '../../components/share-dialog-social-footer/share-dialog-social-footer.component';
import { ShareGalleryCollaboratorsComponent } from '../../components/share-gallery-collaborators/share-gallery-collaborators.component';
import { ShareGalleryDialogFooterComponent } from '../../components/share-gallery-dialog-footer/share-gallery-dialog-footer.component';
import { ShareGalleryLinkComponent } from '../../components/share-gallery-link/share-gallery-link.component';

@Component({
  selector: 'app-share-gallery-dialog',
  imports: [
    ShareGalleryCollaboratorsComponent,
    ShareGalleryLinkComponent,
    ShareGalleryDialogFooterComponent,
    SmoothAutoHeightDirective,
    ShareDialogSocialFooterComponent,
    NgTemplateOutlet,
    LoadingIndicatorComponent,
  ],
  animations: [
    trigger('dialogBottomOuter', dialogBottomOuter),
    trigger('dialogBottomInner', dialogBottomInner),
    trigger('dialogInner', dialogInner),
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './share-gallery-dialog.component.html',
  styleUrl: './share-gallery-dialog.component.scss',
})
export class ShareGalleryDialogComponent implements OnGetData {
  dialogName = 'share-gallery-dialog';
  private dialog = inject(DialogService);
  private state = inject(StateService);
  private tenantService = inject(TenantService);
  private galleryService = inject(GalleryService);

  dialogOptions(): DialogOptions {
    return {
      size: 560,
      spacingX: 0,
      spacingY: 0,
      noBackground: true,
    };
  }
  close: () => void;

  private galleryId = new Subject<string>();
  gallery = toSignal(
    this.galleryId.pipe(
      switchMap(galleryId => this.galleryService.getGallery(galleryId, { effectiveAccessLevels: true, noCache: true }))
    )
  );
  mode = signal<'collaborators' | 'link'>('collaborators');
  inviteMode = signal(false);
  height = signal<number>(250);
  isMobile = ScreenSizes.screenSize() === 'mobile';

  publicAccessLevel = toSignal(
    toObservable(this.gallery).pipe(
      filter(Boolean),
      switchMap(gallery => this.galleryService.getEffectivePublicAccessLevel(gallery))
    )
  );

  isTeamMember = computed(() => {
    const gallery = this.gallery();
    if (!gallery) return;
    const tenantId = (gallery.tenant as TenantDto)?.id || (gallery.tenant as string);
    return this.state.tenants?.some(t => t.id === tenantId);
  });

  onGetData(data: { gallery: GalleryDto; mode?: 'link' }, close: () => void) {
    if (this.tenantService.shouldShowWorkspaceDialog(data.gallery)) {
      return this.dialog.open(WorkspaceDialogComponent, {
        onDone: () => {
          this.dialog.open(ShareGalleryDialogComponent, data);
        },
        gallery: data.gallery,
      });
    }

    this.galleryId.next(data.gallery.id);
    this.close = close;
    if (data.mode === 'link') {
      this.mode.set('link');
    }
  }
}
